import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";

/**
 * DictateMicButton — coral floating mic that lets a first-time mobile visitor
 * dictate their vision into the homepage AI Concierge input box.
 *
 * Mirrors the elevated coral Plan FAB used in the bottom navigation, so a
 * thumb-reach action ("Tap to describe your moment") feels native to the
 * platform. Same one-tap conversion pattern Cash App uses for "Pay" —
 * applied to TREL's most distinctive product surface: the AI request box.
 *
 * Implementation
 *   • Uses the standard Web Speech API (`webkitSpeechRecognition` /
 *     `SpeechRecognition`). The vast majority of mobile browsers in 2025/26
 *     support it (Chrome Android, Safari iOS 14.5+, Edge, Samsung Internet).
 *   • On unsupported browsers the component returns `null` so it never
 *     blocks the hero — desktop typists and feature-phone fallbacks see no
 *     visual debt.
 *   • While listening: the FAB pulses + glows; transcript flows into the
 *     parent's textarea on every `onresult` event (interim or final). When
 *     listening ends (user tapped again or browser ended the recognition)
 *     the parent is signalled via `onListenEnd(finalTranscript)` so it can
 *     optionally auto-submit the planning request.
 *
 * Props
 *   - onTranscript(text:string, isFinal:boolean) — fired on every result.
 *   - onListenEnd?(finalText:string)            — fired when recognition stops.
 *   - lang?:string                              — BCP-47 lang (default "en-US").
 *   - testid?:string                            — custom data-testid.
 *   - className?:string                         — extra layout classes.
 */
export default function DictateMicButton({
  onTranscript,
  onListenEnd,
  lang = "en-US",
  testid = "dictate-mic-btn",
  className = "",
}) {
  // Lazy-detect Web Speech API. Some private modes / iframes report it
  // missing at first paint but recover on user interaction; we still gate
  // visibility on the first probe to keep the markup stable.
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState("");
  const recognitionRef = useRef(null);
  const finalTextRef = useRef("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(Boolean(SR));
  }, []);

  // Tear-down: stop any active recognition when the button unmounts. Without
  // this, a navigation-away while listening would leak the mic indicator on
  // some Chrome builds. Cleanup only calls a ref method (no setState).
  useEffect(() => {
    return () => {
      try { recognitionRef.current?.stop(); } catch (_) { /* idempotent */ }
    };
  }, []);

  const start = () => {
    if (!supported) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = lang;
    rec.interimResults = true;
    rec.continuous = false; // user-driven; one utterance per tap

    finalTextRef.current = "";

    rec.onresult = (evt) => {
      // Concatenate interim + final transcripts so the textarea updates as
      // the user speaks. Final segments are stored separately so we can
      // surface the cleanest version to onListenEnd().
      let interim = "";
      let final = "";
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        const r = evt.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      if (final) finalTextRef.current = (finalTextRef.current + " " + final).trim();
      const merged = (finalTextRef.current + " " + interim).trim();
      onTranscript?.(merged, Boolean(final));
    };

    rec.onerror = (evt) => {
      // "no-speech" + "aborted" are benign — silently stop the indicator so
      // the user can retap. "not-allowed" means the visitor denied mic
      // permission; surface that gently so they understand why nothing happened.
      const code = evt?.error || "unknown";
      if (code === "not-allowed" || code === "service-not-allowed") {
        setError("Microphone access blocked. Enable it in your browser settings to dictate.");
      } else if (code !== "no-speech" && code !== "aborted") {
        setError("Couldn't hear you — try again?");
      }
      setListening(false);
    };

    rec.onend = () => {
      setListening(false);
      onListenEnd?.(finalTextRef.current);
    };

    try {
      rec.start();
      recognitionRef.current = rec;
      setListening(true);
      setError("");
    } catch (_) {
      // .start() throws if called while another instance is still active.
      setListening(false);
    }
  };

  const stop = () => {
    try { recognitionRef.current?.stop(); } catch (_) { /* idempotent */ }
  };

  const toggle = () => (listening ? stop() : start());

  if (!supported) return null;

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={toggle}
        data-testid={testid}
        aria-pressed={listening}
        aria-label={listening ? "Stop dictation" : "Tap to dictate your vision"}
        className={`w-14 h-14 rounded-full flex items-center justify-center
                    bg-[#FF7A45] text-white
                    shadow-[0_10px_24px_-8px_rgba(255,122,69,0.65)]
                    ring-4 ring-white
                    transition-transform duration-200 ease-out
                    ${listening ? "animate-pulse scale-105" : "hover:scale-105 active:scale-95"}`}
      >
        {listening ? <MicOff size={22} strokeWidth={2.4}/> : <Mic size={22} strokeWidth={2.4}/>}
      </button>
      {/* Helper caption sits BELOW the FAB so screen readers read the action
          and sighted users see the friendly nudge. Mobile-only voice; on
          desktop the visual is identical but the keyboard is still primary. */}
      <div
        data-testid={`${testid}-label`}
        className={`mt-2 text-[10px] uppercase tracking-[0.14em] font-semibold text-center
                    ${listening ? "text-[#FF7A45]" : "text-[#FF7A45]/85"}`}
      >
        {listening ? "Listening…" : "Tap to dictate"}
      </div>
      {error && (
        <div
          role="alert"
          data-testid={`${testid}-error`}
          className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 text-[11px] text-[#FF7A45] text-center"
        >
          {error}
        </div>
      )}
    </div>
  );
}
